import { z, type ZodType } from '@customerio/hydra-validation';
import type {
  IntegrationsMParticleCreateFields,
  IntegrationsMParticleUpdateFields,
  MParticleIntegration as UIAPIMParticleIntegration,
} from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * A workspace's mParticle connector — the record behind
 * `/integrations/mparticle_out`.
 *
 * One per workspace, so the entity is keyed on the sole record rather than an
 * id. `created_at` / `last_activity_at` stay raw unix seconds, matching the
 * sibling `integration` and `data-out-integration` entities; the connections
 * overview does its own sec→ms conversion. `events` is the one field
 * normalized: the backend omits it for a connector with none selected, and
 * every consumer wants a list to map over.
 *
 * `secret` is deliberately absent. The API accepts it on create and never
 * returns it, so there is nothing to read back.
 */
export type MParticleIntegration = Omit<UIAPIMParticleIntegration, 'events'> & {
  events: string[];
};

// oxlint-disable-next-line typescript/consistent-type-assertions -- entity looseObject pattern; see ARCHITECTURE.md §2
const MParticleIntegrationFromApi = z.looseObject({
  events: z
    .array(z.string())
    .nullish()
    .transform((events) => events ?? []),
}) as unknown as ZodType<MParticleIntegration, unknown>;

/**
 * Connecting takes the mParticle credentials; the settings ride along at their
 * zero values, because the connect form collects only the key and secret.
 *
 * Derived from the generated request type so the write surface can't drift from
 * the spec — and note the two write bodies genuinely differ, unlike data-out's:
 * the credentials are create-only, and the backend requires all three settings
 * on update.
 */
export type MParticleConnectInput = IntegrationsMParticleCreateFields;

/** The settings the config form saves. The credentials are not editable. */
export type MParticleSettingsInput = IntegrationsMParticleUpdateFields;

/** @public */
export const mparticleIntegration = defineEntity<
  MParticleIntegration,
  MParticleSettingsInput,
  void,
  undefined,
  'workspace'
>({
  name: 'mparticle-integration',
  scope: 'workspace',
  pk: (integration) => String(integration.id),
  schema: { read: MParticleIntegrationFromApi },
});

export const mparticleIntegrationKeys = mparticleIntegration.keys;

export function parseMParticleIntegration(raw: UIAPIMParticleIntegration): MParticleIntegration {
  return mparticleIntegration.parse(raw);
}

/**
 * Saving needs at least one event selected. Ember's `canSaveIntegration` paired
 * this with a dirty check, which is the caller's half.
 */
export function canSaveMParticleSettings(events: readonly string[]): boolean {
  return events.length > 0;
}

/**
 * Whether the enable toggle is stuck off.
 *
 * Only a connector that is *currently off* with nothing selected is locked —
 * turning one off is always allowed. A record can be enabled with an empty event
 * list (the backend's own guard only runs on write), and locking the toggle then
 * would leave no way to switch it off. Unchecking every event while enabled
 * reaches the same state through the UI.
 */
export function isMParticleEnableLocked(settings: { enabled: boolean; events: readonly string[] }): boolean {
  return !settings.enabled && settings.events.length === 0;
}
